import { useEffect, type RefObject } from "react";

export const useCarouselScroll = ({
  containerRef,
  enabled,
  activeChapterId,
  contactExpanded,
  isContactMode,
}: {
  containerRef: RefObject<HTMLElement | null>;
  enabled: boolean;
  activeChapterId: string;
  contactExpanded: boolean;
  isContactMode: boolean;
}) => {
  useEffect(() => {
    if (!enabled || isContactMode || contactExpanded) return;
    const container = containerRef.current;
    if (!container) return;

    const chapterElements = Array.from(
      container.querySelectorAll<HTMLElement>(".scroll-chapter"),
    );
    if (!chapterElements.length) return;

    let currentIndex = Math.max(
      0,
      chapterElements.findIndex(
        (chapter) => chapter.dataset.chapterId === activeChapterId,
      ),
    );
    let lastScrollTime = 0;
    let touchStartY = 0;
    let isTouching = false;

    const scrollToIndex = (nextIndex: number) => {
      const clamped = Math.max(0, Math.min(nextIndex, chapterElements.length - 1));
      if (clamped === currentIndex) return;
      currentIndex = clamped;
      chapterElements[clamped]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    };

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      const now = performance.now();
      if (now - lastScrollTime < 550) return;
      const delta = event.deltaY;
      if (Math.abs(delta) < 24) return;
      lastScrollTime = now;
      scrollToIndex(delta > 0 ? currentIndex + 1 : currentIndex - 1);
    };

    const handleTouchStart = (event: TouchEvent) => {
      if (!event.touches[0]) return;
      isTouching = true;
      touchStartY = event.touches[0].clientY;
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (!isTouching) return;
      event.preventDefault();
    };

    const handleTouchEnd = (event: TouchEvent) => {
      if (!isTouching) return;
      isTouching = false;
      const touch = event.changedTouches[0];
      if (!touch) return;
      const delta = touchStartY - touch.clientY;
      if (Math.abs(delta) < 50) return;
      scrollToIndex(delta > 0 ? currentIndex + 1 : currentIndex - 1);
    };

    container.addEventListener("wheel", handleWheel, { passive: false });
    container.addEventListener("touchstart", handleTouchStart, { passive: true });
    container.addEventListener("touchmove", handleTouchMove, { passive: false });
    container.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener("wheel", handleWheel);
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
    };
  }, [activeChapterId, enabled, contactExpanded, isContactMode, containerRef]);
};
